import { z } from 'zod';

/**
 * Loose UUID schema -- accepts the canonical 8-4-4-4-12 hyphenated hex format
 * with any version/variant nibble, including nil (version 0) and non-RFC
 * versions used in seed/test data.
 * Zod v4 z.string().uuid() enforces versions 1--8 only.
 */
export const uuidSchema = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    'Invalid UUID'
  );

/**
 * Request-side nullable/optional datetime field.
 * Accepts an ISO 8601 datetime string, null, or undefined.
 * The value remains a string — conversion to Date happens in the repo layer.
 */
export const nullableDateSchema = z.string().datetime({ offset: true, local: true }).nullable().optional();

/**
 * Valid blood type values.
 */
export const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] as const;
export const bloodTypeSchema = z.enum(BLOOD_TYPES);

export const ListResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema)
  });

/**
 * AWS Cognito-compliant password schema.
 * Requirements (matches Cognito default password policy):
 *   - Minimum 12 characters
 *   - No leading or trailing spaces
 *   - At least one uppercase letter (A-Z)
 *   - At least one lowercase letter (a-z)
 *   - At least one digit (0-9)
 *   - At least one special character
 */
export const cognitoPasswordSchema = z
  .string()
  .min(12, 'Password must be at least 12 characters')
  .refine((val) => val === val.trim(), 'Password must not contain leading or trailing spaces')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character');

/**
 * Generic API error response -- mirrors ApiErrorResponse from @tms-phoenix/service-core.
 * Used as the response schema for 4xx/5xx status codes in route definitions.
 */
export const ErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
  meta: z.object({
    requestId: z.string(),
    timestamp: z.string(),
  }),
});

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

/** Discriminates which Cognito user pool / tenant lookup path to use. */
export const LoginTypeSchema = z.enum(['donor']).optional();
export type LoginType = z.infer<typeof LoginTypeSchema>;
